import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, homePathFor } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Store, Loader2 } from "lucide-react";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate(homePathFor(data), { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center"><Store className="w-5 h-5" /></div>
          <span className="font-heading font-extrabold text-xl">KASIRKU</span>
        </Link>
        <div>
          <h2 className="font-heading text-4xl font-bold leading-tight">Selamat datang kembali.</h2>
          <p className="mt-4 text-slate-300 max-w-md">Masuk untuk mengelola toko, memproses transaksi, dan melihat laporan penjualan Anda.</p>
        </div>
        <div className="text-slate-400 text-sm">© 2026 KASIRKU</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center text-white"><Store className="w-5 h-5" /></div>
            <span className="font-heading font-extrabold text-xl">KASIRKU</span>
          </Link>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Masuk ke akun</h1>
          <p className="mt-2 text-muted-foreground text-sm">Masukkan email dan kata sandi Anda.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="nama@email.com" className="mt-1.5 rounded-lg" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Kata Sandi</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="mt-1.5 rounded-lg" data-testid="login-password-input" />
            </div>
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full rounded-lg h-11" data-testid="login-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Masuk"}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px bg-border flex-1" /> ATAU <div className="h-px bg-border flex-1" />
          </div>
          <Button variant="outline" onClick={googleLogin} className="w-full rounded-lg h-11" data-testid="google-login-btn">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4 mr-2" /> Masuk dengan Google
          </Button>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Belum punya akun? <Link to="/register" className="text-primary font-semibold" data-testid="to-register-link">Daftar gratis</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
